import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatHorizonNumber, formatServiceDate } from './horizonFormat';
import {
  describeDates,
  describeOutlets,
  type HorizonSignalGroup,
} from './horizonSignalGroups';

/**
 * One finding, however many dates it covers. The scanner emits a card per
 * outlet-date, so showing them raw repeats the same sentence; dates become chips.
 */
export function HorizonSignalGroupRow({
  group,
  selectedDate,
  showOutlets,
  outletName,
  onPickDate,
}: {
  group: HorizonSignalGroup;
  selectedDate: string | null;
  showOutlets: boolean;
  outletName: (id?: string | null) => string | null;
  onPickDate?: (serviceDate: string | null) => void;
}) {
  const { t } = useTranslation('horizon');
  const [expanded, setExpanded] = useState(false);
  const where = showOutlets ? describeOutlets(group.outletIds, outletName) : null;
  const shown = expanded ? group.dates : group.dates.slice(0, 4);
  const hidden = group.dates.length - shown.length;

  return (
    <li className="hz-signal-group">
      <p className="hz-signal-insight">{group.insight}</p>
      <p className="hz-signal-act">{group.recommendedAction}</p>
      <p className="hz-signal-scope">
        {[
          group.span
            ? t('signals.span', {
                start: formatServiceDate(group.span.start),
                end: formatServiceDate(group.span.end),
              })
            : describeDates(group.dates),
          where,
          group.coversAtStake
            ? t('signals.atStake', { covers: formatHorizonNumber(Math.round(group.coversAtStake)) })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {group.dates.length > 0 && (
        <div className="hz-signal-dates">
          {shown.map(date => (
            <button
              key={date}
              type="button"
              className={`hz-date-chip${date === selectedDate ? ' is-on' : ''}`}
              onClick={() => onPickDate?.(date)}
            >
              {formatServiceDate(date)}
            </button>
          ))}
          {hidden > 0 && (
            <button
              type="button"
              className="hz-date-chip is-more"
              onClick={() => setExpanded(true)}
            >
              {t('signals.more', { count: hidden })}
            </button>
          )}
        </div>
      )}
    </li>
  );
}
